import { useCallback, useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'

export const useFitToPage = () => {
  return useCallback(agGrid => {
    // A destroyed grid's API answers undefined.
    if (agGrid.api && !agGrid.api.isDestroyed?.()) {
      let columnState = (agGrid.api.getColumnState() || []).filter(column => column.colId !== 'menu' && !column.pinned)

      // A column that declares its own flex (table-columns.js) keeps it.
      columnState.forEach(column => {
        column.flex = column.flex || 1
      })

      agGrid.api.applyColumnState({ state: columnState })
    }
  }, [])
}

export const useFitToColumns = () => {
  return useCallback(agGrid => {
    if (agGrid.api && !agGrid.api.isDestroyed?.()) {
      let columnState = agGrid.api.getColumnState()
      let columns = agGrid.api.getColumns()

      if (columns) {
        let sizeColumns = columns.filter(column => column.colId !== 'menu').map(column => column.getId())
        agGrid.api.autoSizeColumns(sizeColumns, false)
      }

      agGrid.api.applyColumnState(columnState)
    }
  }, [])
}

// A grid persists its quick filter, sort and scroll position to the query
// string. Two grids on ONE page therefore used to read and write the same four
// keys: sorting one suppressed the other's default sort, and a filter typed in
// one copied into the other on the next rowDataUpdated.
//
// `queryKey` namespaces them per grid. It is optional and an omitted/empty
// value keeps exactly the historical unprefixed keys, so every existing page --
// which only ever has one grid -- keeps its current URLs and bookmarks.
export const getQueryKeys = queryKey => {
  const prefix = queryKey ? `${queryKey}.` : ''

  return {
    search: `${prefix}search`,
    index: `${prefix}index`,
    sortCol: `${prefix}sortCol`,
    sortDir: `${prefix}sortDir`
  }
}

export const useGetQueryValues = queryKey => {
  return useCallback(() => {
    const keys = getQueryKeys(queryKey)
    const searchValues = new URLSearchParams(document.location.search)

    return {
      search: searchValues.get(keys.search) || '',
      index: parseInt(searchValues.get(keys.index) || 0),
      sortCol: searchValues.get(keys.sortCol),
      sortDir: searchValues.get(keys.sortDir)
    }
  }, [queryKey])
}

export const useSetQueryValues = queryKey => {
  const history = useHistory()
  const timerRef = useRef(null)

  // A held scroll-only write must not land after the grid has gone: it
  // would write the old search back over the page the reader moved to
  // (an Insights pivot pushes a new URL and unmounts the grid).
  useEffect(() => () => clearTimeout(timerRef.current), [])

  return useCallback((search, index, sortCol, sortDir) => {
    const keys = getQueryKeys(queryKey)
    const searchValues = new URLSearchParams(document.location.search)

    if (search !== undefined) {
      if (search) {
        searchValues.set(keys.search, search)
      } else {
        searchValues.delete(keys.search)
      }
    }

    if (index !== undefined) {
      searchValues.set(keys.index, index)
    }

    if (sortCol !== undefined) {
      if (sortCol) {
        searchValues.set(keys.sortCol, sortCol)
      } else {
        searchValues.delete(keys.sortCol)
      }
    }

    if (sortDir !== undefined) {
      if (sortDir) {
        searchValues.set(keys.sortDir, sortDir)
      } else {
        searchValues.delete(keys.sortDir)
      }
    }

    // Each write keeps the location's route state (e.g. the Request log's
    // { from } that offers "← Back to the audit trail"): replace() with only
    // a search would drop it.
    // Scroll-only updates (only index changed) are debounced to avoid
    // exceeding browser's history.replaceState rate limit (100/10s).
    const isScrollOnly = search === undefined && sortCol === undefined && sortDir === undefined
    if (isScrollOnly) {
      clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => {
        history.replace({ search: `${searchValues}`, state: history.location.state })
      }, 150)
    } else {
      clearTimeout(timerRef.current)
      history.replace({ search: `${searchValues}`, state: history.location.state })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey])
}
